import { tokens } from '@healthdesk/shared-utils';
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Drawer, List, ListItemButton, ListItemText } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { SplitFlapRow } from "../../features/facility/components/queue/splitflap/QueueDisplay";
import {
  SplitFlapClipPathVariant,
  SlotReelVariant,
  FlipClockVariant,
  SlideVariant,
} from "./SplitFlapVariants";
import "./MSI3SplashPage.css";

/** Bar height for splash top bar (matches other app bars). */
const SPLASH_BAR_HEIGHT = 56;
/** Max content width on splash (matches `.msi3-splash-shell`) for aligning the menu with the page grid. */
const SPLASH_CONTENT_MAX_WIDTH_PX = 1200;
/** Extra padding past safe-area on small viewports (px). */
const SPLASH_MENU_LEFT_INSET_COMPACT_PX = 20;
/** Matches `.msi3-splash-page` horizontal padding when aligning with centered shell on wide screens. */
const SPLASH_PAGE_HORIZONTAL_PAD_PX = 24;
/** Main content top offset: bar + iOS safe area. */
const SPLASH_MAIN_TOP = `calc(${SPLASH_BAR_HEIGHT}px + env(safe-area-inset-top, 0px))`;

/** 1st: ChartMind (professional), 2nd: MasterChart (Patient), 3rd: Savoir-Care (Facility) */
const BRANDS = [
  { text: "CHARTMIND", color: tokens.colors.msiRed.light },
  { text: "MASTERCHART", color: tokens.colors.msiGreen.light },
  { text: "SAVOIR-CARE", color: "secondary.main" },
];

const CHARTMIND_COLOR = tokens.colors.msiRed.light;
const VARIANT_LABELS = [
  "1. Single-face flip (current)",
  "2. True split-flap (clip-path)",
  "3. Slot reel",
  "4. Flip clock",
  "5. Slide",
];

/** Default timing (ms) for splash flap animation */
const DEFAULT_FLAP_SPEED_MS = 95;
const DEFAULT_CHAR_STAGGER_MS = 160;
const DEFAULT_FLIP_DURATION_MS = 120;

const MIN_FLAP_SPEED = 20;
const MAX_FLAP_SPEED = 250;
const MIN_STAGGER = 30;
const MAX_STAGGER = 400;
const MIN_FLIP_DURATION = 40;
const MAX_FLIP_DURATION = 250;

function parseClamp(str, min, max, fallback) {
  const n = parseInt(String(str).trim(), 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

const MSI3SplashPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [flapSpeedInput, setFlapSpeedInput] = useState(String(DEFAULT_FLAP_SPEED_MS));
  const [charStaggerInput, setCharStaggerInput] = useState(String(DEFAULT_CHAR_STAGGER_MS));
  const [flipDurationInput, setFlipDurationInput] = useState(String(DEFAULT_FLIP_DURATION_MS));
  const [animationKey, setAnimationKey] = useState(0);
  const [variantReplayKeys, setVariantReplayKeys] = useState([0, 0, 0, 0, 0]);

  const handleVariantReplay = (i) => {
    setVariantReplayKeys((prev) => {
      const next = [...prev];
      next[i] = (next[i] ?? 0) + 1;
      return next;
    });
  };

  const flapSpeedMs = parseClamp(flapSpeedInput, MIN_FLAP_SPEED, MAX_FLAP_SPEED, DEFAULT_FLAP_SPEED_MS);
  const charStaggerMs = parseClamp(charStaggerInput, MIN_STAGGER, MAX_STAGGER, DEFAULT_CHAR_STAGGER_MS);
  const flipDurationMs = parseClamp(flipDurationInput, MIN_FLIP_DURATION, MAX_FLIP_DURATION, DEFAULT_FLIP_DURATION_MS);

  const handleEnter = () => {
    navigate("/auth", { replace: true });
  };

  const handleRerunAnimation = () => {
    setAnimationKey((k) => k + 1);
  };

  const handleResetTiming = () => {
    setFlapSpeedInput(String(DEFAULT_FLAP_SPEED_MS));
    setCharStaggerInput(String(DEFAULT_CHAR_STAGGER_MS));
    setFlipDurationInput(String(DEFAULT_FLIP_DURATION_MS));
    setAnimationKey((k) => k + 1);
  };

  const normalizeOnBlur = (setter, value, min, max, fallback) => {
    const n = parseClamp(value, min, max, fallback);
    setter(String(n));
  };

  const handleMenuClose = () => setMenuOpen(false);
  const handleNav = (path) => {
    setMenuOpen(false);
    navigate(path, { replace: true });
  };

  const toolbarPaddingLeft = {
    xs: `calc(env(safe-area-inset-left, 0px) + ${SPLASH_MENU_LEFT_INSET_COMPACT_PX}px)`,
    md: `calc(env(safe-area-inset-left, 0px) + max(${SPLASH_PAGE_HORIZONTAL_PAD_PX}px, (100vw - ${SPLASH_CONTENT_MAX_WIDTH_PX}px) / 2 + ${SPLASH_PAGE_HORIZONTAL_PAD_PX}px))`,
  };

  return (
    <>
      {/*
        AppBar + Drawer live outside `.msi3-splash-root` so `overflow: hidden` on the root
        does not clip `position: fixed` chrome (which was eating the left side of the menu icon on desktop).
      */}
      <AppBar
        className="msi3-splash-app-bar"
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: tokens.colors.msiRed.light,
          boxShadow: "none",
          border: 0,
          backgroundImage: "none",
          paddingTop: "env(safe-area-inset-top, 0px)",
          "& .MuiToolbar-root": {
            border: 0,
            boxShadow: "none",
            backgroundImage: "none",
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: SPLASH_BAR_HEIGHT,
            height: SPLASH_BAR_HEIGHT,
            paddingLeft: toolbarPaddingLeft,
            paddingRight: 16,
          }}
        >
          <IconButton
            aria-label="open menu"
            edge={false}
            onClick={() => setMenuOpen(true)}
            sx={{ color: "common.white", marginLeft: 0, marginRight: 0 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <List sx={{ pt: 2 }}>
          <ListItemButton onClick={() => handleNav("/")}>
            <ListItemText primary="Home (Map)" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNav("/auth")}>
            <ListItemText primary="Enter Platform" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNav("/splash/chartmind")}>
            <ListItemText primary="Split-Flap Display (CHARTMIND)" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNav("/splash/title-logos")}>
            <ListItemText primary="Sample title logos" />
          </ListItemButton>
        </List>
      </Drawer>
      <div className="msi3-splash-root">
      <div className="msi3-splash-page" style={{ paddingTop: SPLASH_MAIN_TOP }}>
        <div className="msi3-splash-shell">
          <div className="msi3-splash-brand-lockup">
            <div className="msi3-splash-msi-row-wrap">
              <div className="msi3-splash-msi-row" aria-label="MSI cubed logo">
                <SplitFlapRow
                  textSegments={["MSI"]}
                  speeds={[flapSpeedMs]}
                  rowIndex={0}
                  isClock={true}
                  compact={false}
                  segmentColors={["primary.main"]}
                  segmentBgColor="transparent"
                  charStaggerMs={charStaggerMs}
                  flipDurationMs={flipDurationMs}
                  className="msi3-splash-msi-flap-row"
                  tileWidth="clamp(78px, 10vw, 110px)"
                  tileHeight="clamp(96px, 12vw, 128px)"
                />
                <div className="msi3-splash-msi-tile msi3-splash-msi-tile--superscript">
                  <div className="msi3-splash-upper-flap" aria-hidden="true">
                    <sup className="msi3-splash-superscript">3</sup>
                  </div>
                </div>
              </div>
            </div>
            <p className="msi3-splash-headline">Medical SuperIntelligence</p>
          </div>

          <div className="msi3-splash-description">
            <p>
              Medical SuperIntelligence is a human aspiration, not a machine.
              Intelligence — genuine intelligence in health care — is a uniquely human capacity. It encompasses not just computation and pattern recognition, but judgment, wisdom, presence, moral courage, compassion, and the ability to hold another person&apos;s suffering with grace. These are not features that can be artificially replicated, and they are what clinical intelligence actually consists of.
            </p>
            <p>
              Artificial intelligence is, by definition, artificial. It is an extraordinarily powerful tool — capable of processing decades of health data in seconds, detecting patterns invisible to the unaided eye, eliminating documentation burden, and supporting clinical decision-making at scale. But it is a tool. It does not understand. It does not care. It does not heal.
            </p>
            <p>
              MSI³ is built on this distinction.
            </p>
            <p>
              The platform&apos;s purpose is not to replace clinical intelligence — it is to amplify it. By absorbing the computational labor that currently consumes nearly half of a physician&apos;s working day, MSI³ gives clinicians back the cognitive and emotional bandwidth to practice at the full depth of human capability. The patient&apos;s MasterChart extends this further by creating continuity of care — facilitating universal access to clinical documentation by every provider, regardless of EHR or institutional affiliation.
            </p>
            <p>
              Medical SuperIntelligence³, then, is the aspiration of the (1) <strong>patient&apos;s</strong> visit with (2) <strong>clinicians</strong>, and it can be enhanced by the (3) <strong>facility</strong>.
            </p>
            <p>
              It is what emerges in the encounter between a clinician and a patient when artificial intelligence handles what machines do well, freeing humans to bring everything that only humans can bring. The &quot;super&quot; in superintelligence doesn&apos;t describe the AI. It describes what human intelligence is capable of when it is no longer buried under information overload and administrative burden — when the clinician is finally free to see, to understand, to help.
            </p>
          </div>

          <div className="msi3-splash-board-wrap">
            <div className="msi3-splash-board-label">Platform Suite</div>
            <div className="msi3-splash-board msi3-splash-board--multi" aria-live="polite">
              {BRANDS.map((brand, index) => (
                <div key={`${brand.text}-${animationKey}`} className="msi3-splash-brand-display">
                  <SplitFlapRow
                    textSegments={[brand.text]}
                    speeds={[flapSpeedMs]}
                    rowIndex={index}
                    isClock={true}
                    compact={false}
                    segmentColors={[brand.color]}
                    charStaggerMs={charStaggerMs}
                    flipDurationMs={flipDurationMs}
                  />
                </div>
              ))}
            </div>

            <div className="msi3-splash-timing-panel">
              <div className="msi3-splash-timing-title">Flap timing</div>
              <div className="msi3-splash-timing-grid">
                <label className="msi3-splash-timing-label">
                  <span>Delay between flips (ms)</span>
                  <input
                    type="number"
                    min={MIN_FLAP_SPEED}
                    max={MAX_FLAP_SPEED}
                    value={flapSpeedInput}
                    onChange={(e) => setFlapSpeedInput(e.target.value)}
                    onBlur={() => normalizeOnBlur(setFlapSpeedInput, flapSpeedInput, MIN_FLAP_SPEED, MAX_FLAP_SPEED, DEFAULT_FLAP_SPEED_MS)}
                  />
                </label>
                <label className="msi3-splash-timing-label">
                  <span>Stagger between chars (ms)</span>
                  <input
                    type="number"
                    min={MIN_STAGGER}
                    max={MAX_STAGGER}
                    value={charStaggerInput}
                    onChange={(e) => setCharStaggerInput(e.target.value)}
                    onBlur={() => normalizeOnBlur(setCharStaggerInput, charStaggerInput, MIN_STAGGER, MAX_STAGGER, DEFAULT_CHAR_STAGGER_MS)}
                  />
                </label>
                <label className="msi3-splash-timing-label">
                  <span>Flip duration (ms)</span>
                  <input
                    type="number"
                    min={MIN_FLIP_DURATION}
                    max={MAX_FLIP_DURATION}
                    value={flipDurationInput}
                    onChange={(e) => setFlipDurationInput(e.target.value)}
                    onBlur={() => normalizeOnBlur(setFlipDurationInput, flipDurationInput, MIN_FLIP_DURATION, MAX_FLIP_DURATION, DEFAULT_FLIP_DURATION_MS)}
                  />
                </label>
              </div>
              <div className="msi3-splash-timing-actions">
                <button
                  type="button"
                  className="msi3-splash-btn msi3-splash-btn-secondary"
                  onClick={handleRerunAnimation}
                >
                  Re-run animation
                </button>
                <button
                  type="button"
                  className="msi3-splash-btn msi3-splash-btn-secondary"
                  onClick={handleResetTiming}
                >
                  Reset to defaults
                </button>
              </div>
            </div>

            <div className="msi3-splash-variants">
              <div className="msi3-splash-variants-title">Display variants — “CHARTMIND”</div>
              <div className="msi3-splash-variant" key={`v1-${animationKey}-${variantReplayKeys[0]}`}>
                <div className="msi3-splash-variant-label-row">
                  <span className="msi3-splash-variant-label">{VARIANT_LABELS[0]}</span>
                  <button
                    type="button"
                    className="msi3-splash-btn msi3-splash-btn-secondary msi3-splash-variant-replay"
                    onClick={() => handleVariantReplay(0)}
                  >
                    Replay
                  </button>
                </div>
                <div className="msi3-splash-variant-display">
                  <SplitFlapRow
                    textSegments={["CHARTMIND"]}
                    speeds={[flapSpeedMs]}
                    rowIndex={0}
                    isClock={true}
                    compact={false}
                    segmentColors={[CHARTMIND_COLOR]}
                    charStaggerMs={charStaggerMs}
                    flipDurationMs={flipDurationMs}
                  />
                </div>
              </div>
              <div className="msi3-splash-variant" key={`v2-${animationKey}-${variantReplayKeys[1]}`}>
                <div className="msi3-splash-variant-label-row">
                  <span className="msi3-splash-variant-label">{VARIANT_LABELS[1]}</span>
                  <button
                    type="button"
                    className="msi3-splash-btn msi3-splash-btn-secondary msi3-splash-variant-replay"
                    onClick={() => handleVariantReplay(1)}
                  >
                    Replay
                  </button>
                </div>
                <div className="msi3-splash-variant-display">
                  <SplitFlapClipPathVariant text="CHARTMIND" color={CHARTMIND_COLOR} animationKey={variantReplayKeys[1]} />
                </div>
              </div>
              <div className="msi3-splash-variant" key={`v3-${animationKey}-${variantReplayKeys[2]}`}>
                <div className="msi3-splash-variant-label-row">
                  <span className="msi3-splash-variant-label">{VARIANT_LABELS[2]}</span>
                  <button
                    type="button"
                    className="msi3-splash-btn msi3-splash-btn-secondary msi3-splash-variant-replay"
                    onClick={() => handleVariantReplay(2)}
                  >
                    Replay
                  </button>
                </div>
                <div className="msi3-splash-variant-display">
                  <SlotReelVariant text="CHARTMIND" color={CHARTMIND_COLOR} animationKey={variantReplayKeys[2]} />
                </div>
              </div>
              <div className="msi3-splash-variant" key={`v4-${animationKey}-${variantReplayKeys[3]}`}>
                <div className="msi3-splash-variant-label-row">
                  <span className="msi3-splash-variant-label">{VARIANT_LABELS[3]}</span>
                  <button
                    type="button"
                    className="msi3-splash-btn msi3-splash-btn-secondary msi3-splash-variant-replay"
                    onClick={() => handleVariantReplay(3)}
                  >
                    Replay
                  </button>
                </div>
                <div className="msi3-splash-variant-display">
                  <FlipClockVariant text="CHARTMIND" color={CHARTMIND_COLOR} animationKey={variantReplayKeys[3]} />
                </div>
              </div>
              <div className="msi3-splash-variant" key={`v5-${animationKey}-${variantReplayKeys[4]}`}>
                <div className="msi3-splash-variant-label-row">
                  <span className="msi3-splash-variant-label">{VARIANT_LABELS[4]}</span>
                  <button
                    type="button"
                    className="msi3-splash-btn msi3-splash-btn-secondary msi3-splash-variant-replay"
                    onClick={() => handleVariantReplay(4)}
                  >
                    Replay
                  </button>
                </div>
                <div className="msi3-splash-variant-display">
                  <SlideVariant text="CHARTMIND" color={CHARTMIND_COLOR} animationKey={variantReplayKeys[4]} />
                </div>
              </div>
            </div>

            <div className="msi3-splash-controls">
              <button
                type="button"
                className="msi3-splash-btn msi3-splash-btn-primary"
                onClick={handleEnter}
              >
                Enter Platform
              </button>
            </div>

            <p className="msi3-splash-hint">
              ChartMind (professional), MasterChart (Patient), Savoir-Care (Facility).
            </p>
            <Link to="/splash/chartmind" className="msi3-splash-alt-link">
              Split-Flap Display (CHARTMIND)
            </Link>
            <Link to="/splash/title-logos" className="msi3-splash-alt-link">
              Sample title logos
            </Link>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default MSI3SplashPage;
